import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  Output,
  inject,
} from '@angular/core';
import { ISirena as ISirenaG, ICliente } from 'modelos/src';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import { LoginService } from '../../../../login/login.service';
import { SirenaMapa } from '../mapa-sirenas.component';

@Component({
  selector: 'app-flotante-sirenas',
  templateUrl: './flotante-sirenas.component.html',
  styleUrl: './flotante-sirenas.component.scss',
  standalone: false,
})
export class FlotanteSirenasComponent implements OnInit, OnDestroy, OnChanges {
  helper = inject(HelperService);

  @Input() sirenas?: SirenaMapa[] = [];
  @Output() sirenaChange = new EventEmitter<SirenaMapa[]>();
  @Output() clusterChange = new EventEmitter<boolean>(); // Cambios en agrupamiento

  public clientePropio = LoginService.getCliente();
  public expansionPanelExpanded = true; // Estado del expansion panel (por defecto expandido)
  public clusterEnabled = false;

  // Filtros activos
  public filtrosEstado: string[] = [];

  // Chips de estado del panel (la clave es la de sirenaCumpleFiltro)
  public readonly estados = [
    { clave: 'Online', label: 'Online', icono: 'cloud_done' },
    { clave: 'Offline', label: 'Offline', icono: 'cloud_off' },
    { clave: 'Sonido encendido', label: 'Sonido', icono: 'volume_up' },
    { clave: 'Luz encendida', label: 'Luz', icono: 'light_mode' },
    { clave: 'Con errores', label: 'Con errores', icono: 'error' },
    { clave: 'Actualizando', label: 'Actualizando', icono: 'sync' },
    { clave: 'Con camara', label: 'Con cámara', icono: 'videocam' },
  ];
  public filtrosCliente: string[] = [];

  // Filtros de búsqueda
  public searchTerm: string = ''; // Búsqueda por dirección
  public sirenasFiltradas: SirenaMapa[] = [];
  public sirenasSeleccionadas: SirenaMapa[] = [];

  public clientes?: ICliente[] = [];
  public clientesVisibles?: ICliente[];

  // Cache de contadores para optimización
  private contadoresClienteCache = new Map<string, number>();

  // Getter para comprobar si es dispositivo móvil
  get isMobile(): boolean {
    return window.innerWidth <= 768;
  }

  // Listener para cambios de tamaño de ventana
  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    const wasMobile = !this.expansionPanelExpanded;
    const isMobileNow = event.target.innerWidth <= 768;

    // Solo cambiar el estado si cambiamos entre móvil y escritorio
    if (wasMobile !== isMobileNow) {
      this.expansionPanelExpanded = !isMobileNow;
    }
  }

  private getBooleanFromStorage(key: string, defaultValue: boolean): boolean {
    const value = localStorage.getItem(key);
    return value !== null ? value === 'true' : defaultValue;
  }

  // ========== FILTRADO ==========

  public get hayFiltrosActivos(): boolean {
    return (
      this.searchTerm.length >= 2 ||
      this.filtrosCliente.length > 0 ||
      this.sirenasSeleccionadas.length > 0 ||
      this.filtrosEstado.length > 0
    );
  }

  private getOnlineState(sirena: SirenaMapa): boolean | undefined {
    if (sirena.sirena?.online !== undefined) {
      return sirena.sirena.online;
    }

    if (sirena.estado === 'Online') {
      return true;
    }

    if (sirena.estado === 'Offline') {
      return false;
    }

    return undefined;
  }

  private getDireccionParaBusqueda(sirena: SirenaMapa): string {
    return (
      sirena.sirena?.direccionGps ||
      sirena.direccion ||
      ''
    ).toLowerCase();
  }

  public filtrarSirenas(): void {
    let resultado = this.sirenas || [];

    // Filtro por búsqueda de dirección
    if (this.searchTerm.length >= 2) {
      const termino = this.searchTerm.toLowerCase();
      resultado = resultado.filter((s) =>
        this.getDireccionParaBusqueda(s).includes(termino),
      );
    }

    // Filtro por sirenas seleccionadas manualmente
    if (this.sirenasSeleccionadas.length > 0) {
      const ids = new Set(this.sirenasSeleccionadas.map((s) => s._id));
      resultado = resultado.filter((s) => ids.has(s._id));
    }

    // Filtro por clientes
    if (this.filtrosCliente.length > 0) {
      resultado = resultado.filter(
        (s) => s.idCliente && this.filtrosCliente.includes(s.idCliente),
      );
    }

    // Filtro por estados
    if (this.filtrosEstado.length > 0) {
      resultado = resultado.filter((s) => this.sirenaCumpleFiltro(s));
    }

    this.sirenasFiltradas = resultado;
    this.sirenaChange.emit(resultado);
  }

  public toggleEstado(estado: string) {
    const index = this.filtrosEstado.indexOf(estado);
    if (index > -1) {
      this.filtrosEstado.splice(index, 1);
    } else {
      this.filtrosEstado.push(estado);
    }
    this.filtrarSirenas();
  }

  public limpiarFiltros(): void {
    this.searchTerm = '';
    this.sirenasSeleccionadas = [];
    this.filtrosCliente = [];
    this.filtrosEstado = [];
    this.filtrarSirenas();
  }

  public limpiarEstados(): void {
    this.filtrosEstado = [];
    this.filtrarSirenas();
  }

  public limpiarBusqueda(): void {
    this.searchTerm = '';
    this.filtrarSirenas();
  }

  public onBuscarDireccion(): void {
    this.filtrarSirenas();
  }

  public onChangeFiltroClientes(): void {
    this.filtrarSirenas();
  }

  public seleccionarTodosClientes(): void {
    this.filtrosCliente = this.clientesVisibles?.map((c) => c._id) || [];
    this.onChangeFiltroClientes();
  }

  public deseleccionarTodosClientes(): void {
    this.filtrosCliente = [];
    this.onChangeFiltroClientes();
  }

  /** Alterna agrupamiento y emite evento */
  public onToggleCluster() {
    this.clusterEnabled = !this.clusterEnabled;
    this.clusterChange.emit(this.clusterEnabled);
    localStorage.setItem('mostrarClusterSirena', String(this.clusterEnabled));
  }

  // ========== CONTADORES ==========

  get sirenasVisibles(): SirenaMapa[] {
    return this.sirenasFiltradas.length > 0 || this.hayFiltrosActivos
      ? this.sirenasFiltradas
      : this.sirenas || [];
  }

  public sirenaCumpleFiltro(sirena: SirenaMapa): boolean {
    if (this.filtrosEstado.length === 0) return true;

    const onlineState = this.getOnlineState(sirena);

    const condiciones: { [key: string]: boolean } = {
      Online: onlineState === true,
      Offline: onlineState === false,
      'Sonido encendido': sirena.sirena?.sonidoEncendido === true,
      'Luz encendida': sirena.sirena?.luzEncendida === true,
      'Con errores': sirena.sirena?.errorActualizacion === true,
      Actualizando: sirena.sirena?.actualizando === true,
      'Con camara': (sirena.camaras?.length || 0) > 0,
    };

    return this.filtrosEstado.some((estado) => condiciones[estado]);
  }

  public contarSirenasPorEstadoTotal(estado: string): number {
    if (!this.sirenas) return 0;

    // Aplicar primero los filtros de búsqueda, identificación y cliente
    let sirenasFiltradas = this.sirenas;

    // Filtro por búsqueda de dirección
    if (this.searchTerm.length >= 2) {
      const termino = this.searchTerm.toLowerCase();
      sirenasFiltradas = sirenasFiltradas.filter((s) =>
        this.getDireccionParaBusqueda(s).includes(termino),
      );
    }

    // Filtro por clientes
    if (this.filtrosCliente.length > 0) {
      sirenasFiltradas = sirenasFiltradas.filter(
        (s) => s.idCliente && this.filtrosCliente.includes(s.idCliente),
      );
    }

    // Filtro por sirenas seleccionadas manualmente
    if (this.sirenasSeleccionadas.length > 0) {
      const ids = new Set(this.sirenasSeleccionadas.map((s) => s._id));
      sirenasFiltradas = sirenasFiltradas.filter((s) => ids.has(s._id));
    }

    // Ahora contar según el estado
    return sirenasFiltradas.filter((sirena) => {
      const onlineState = this.getOnlineState(sirena);

      switch (estado) {
        case 'Online':
          return onlineState === true;
        case 'Offline':
          return onlineState === false;
        case 'Sonido encendido':
          return sirena.sirena?.sonidoEncendido === true;
        case 'Luz encendida':
          return sirena.sirena?.luzEncendida === true;
        case 'Con errores':
          return sirena.sirena?.errorActualizacion === true;
        case 'Actualizando':
          return sirena.sirena?.actualizando === true;
        case 'Con camara':
          return sirena.camaras?.length > 0;
        default:
          return false;
      }
    }).length;
  }

  public contarSirenasPorCliente(cliente: ICliente): number {
    if (!this.sirenas) return 0;

    // Usar cache si está disponible
    if (this.contadoresClienteCache.has(cliente._id)) {
      return this.contadoresClienteCache.get(cliente._id)!;
    }

    const count = this.sirenas.filter(
      (s) => s.idCliente === cliente?._id,
    ).length;
    this.contadoresClienteCache.set(cliente._id, count);
    return count;
  }

  // TrackBy function para ng-select
  public trackByClienteId(index: number, cliente: ICliente): string {
    return cliente?._id;
  }

  // Invalidar cache de contadores
  private invalidarCacheContadores(): void {
    this.contadoresClienteCache.clear();
  }

  async ngOnInit(): Promise<void> {
    // Detectar si es dispositivo móvil para el estado inicial del expansion panel
    this.expansionPanelExpanded = window.innerWidth > 768;

    // Inicializar sirenas filtradas
    this.sirenasFiltradas = this.sirenas || [];

    // Cargar estado de agrupamiento desde localStorage y reflejarlo en el toggle
    this.clusterEnabled = this.getBooleanFromStorage(
      'mostrarClusterSirena',
      false,
    );
    // Emitir el estado inicial para sincronizar mapa/UI
    this.clusterChange.emit(this.clusterEnabled);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['sirenas'] && !changes['sirenas'].firstChange) {
      this.invalidarCacheContadores();
      this.filtrarSirenas();
    }
  }

  ngOnDestroy(): void {}
}
